import React, { useEffect, useState } from 'react';
import { api, mediaUrl } from '@/lib/api';
import TemplateAssignmentControl from '@/components/admin/TemplateAssignmentControl';
import { ADMIN } from '@/constants/testIds';
import { Plus, Trash2, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { FileField, TextField, Toggle, Select, Drawer } from '@/components/admin/shared';

const DEFAULT_ORDER = ['watch', 'music', 'podcasts', 'on_my_mind', 'only_on_planet_g', 'collab'];

const DEFAULT_CHANNEL = {
  key: '', label: '', route: '/', order: 99, accent: 'emerald',
  description: '', cta_label: '', image_url: '', enabled: true,
  show_in_nav: true, show_in_carousel: true,
  // Phase 19 — Reusable Audio Experience
  content_experience: 'standard',
};

const EXPERIENCE_OPTS = [
  { value: 'standard', label: 'Standard' },
  { value: 'audio', label: 'Audio' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'video', label: 'Video' },
  { value: 'reading', label: 'Reading' },
  { value: 'product', label: 'Product' },
];

export default function ChannelsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULT_CHANNEL);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/channels'); setItems(data); } catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(DEFAULT_CHANNEL); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...DEFAULT_CHANNEL, ...it }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/channels/${editing.id}`, form);
      else await api.post('/admin/channels', form);
      setOpen(false); await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this channel? It will be removed from nav and the home carousel.')) return;
    try { await api.delete(`/admin/channels/${id}`); await load(); } catch (e) { alert(e?.response?.data?.detail || 'Delete failed'); }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
    try {
      await api.post('/admin/channels/reorder', { order: next.map((c, i) => ({ id: c.id, order: i + 1 })) });
    } catch (e) { alert(e?.response?.data?.detail || 'Reorder failed'); }
  };

  const resetDefaultOrder = async () => {
    if (!window.confirm('Reset channel order to: Watch → Music → Podcasts → On My Mind → Only on Planet G → Collab?')) return;
    const orderMap = Object.fromEntries(DEFAULT_ORDER.map((k, i) => [k, i + 1]));
    const payload = items
      .filter((c) => orderMap[c.key] !== undefined)
      .map((c) => ({ id: c.id, order: orderMap[c.key] }));
    try {
      await api.post('/admin/channels/reorder', { order: payload });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Reset failed'); }
  };

  return (
    <div data-testid="admin-channels-manager">
      <div className="pg-admin-bar flex items-center justify-between mb-4">
        <div>
          <p className="pg-label">CHANNELS / NAVIGATION</p>
          <h3 className="font-serif text-xl mt-1 text-[var(--pg-ink)]">DB-driven destinations</h3>
          <p className="text-xs text-[var(--pg-ink-muted)] mt-1 max-w-2xl">Edit nav, home carousel artwork, and routes. Changes apply site-wide without redeploy.</p>
        </div>
        <button data-testid={ADMIN.createButton} onClick={openCreate} className="pg-btn-primary rounded-[var(--pg-radius-md)] px-4 py-2 text-xs font-mono tracking-widest inline-flex items-center gap-2">
          <Plus size={14} /> NEW CHANNEL
        </button>
      </div>

      <div className="flex items-center justify-end -mt-2 mb-2">
        <button
          onClick={resetDefaultOrder}
          className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-[10px] font-mono tracking-[0.24em] uppercase inline-flex items-center gap-1.5"
          data-testid="reset-channel-order"
        >
          <RotateCcw size={12} /> Reset to default order
        </button>
      </div>

      <div className="pg-card overflow-hidden">
        {loading ? <div className="p-10 text-center text-[var(--pg-ink-muted)]">Loading channels…</div>
        : items.length === 0 ? <div className="p-10 text-center text-[var(--pg-ink-muted)]">No channels yet.</div>
        : (
          <ul className="divide-y divide-[var(--pg-border-2)]">
            {items.map((c, i) => (
              <li key={c.id} className="p-4 flex items-center gap-4">
                <div className="flex flex-col gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] disabled:opacity-30" aria-label="move up"><ArrowUp size={12} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] disabled:opacity-30" aria-label="move down"><ArrowDown size={12} /></button>
                </div>
                <div className="h-14 w-20 rounded-md overflow-hidden bg-[var(--pg-navy-800)] border border-[var(--pg-border-2)] shrink-0">
                  {c.image_url ? <img src={mediaUrl(c.image_url)} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center text-[var(--pg-ink-faint)] text-[10px] font-mono">NO IMG</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-serif text-base text-[var(--pg-ink)] truncate">{c.label}</p>
                    <span className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">/{c.key}</span>
                    {!c.enabled && <span className="text-[10px] font-mono text-[var(--pg-neon-pink)]">HIDDEN</span>}
                  </div>
                  <p className="text-xs text-[var(--pg-ink-muted)] truncate">{c.route} · accent {c.accent}{c.show_in_nav ? '' : ' · not in nav'}{c.show_in_carousel ? '' : ' · not in carousel'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(c)} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs">Edit</button>
                  <button onClick={() => del(c.id)} data-testid={ADMIN.deleteButton} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs text-[var(--pg-neon-pink)]"><Trash2 size={14} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Drawer open={open} onClose={() => setOpen(false)} title={editing ? 'Edit channel' : 'New channel'} onSave={save} saving={saving}>
        <TextField label="Label (display name)" value={form.label} onChange={(v) => setForm({ ...form, label: v })} placeholder="Music" />
        <TextField label="Key (stable slug, no spaces)" value={form.key} onChange={(v) => setForm({ ...form, key: v.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} placeholder="music" />
        <TextField label="Route" value={form.route} onChange={(v) => setForm({ ...form, route: v })} placeholder="/music" />
        <TextField label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea rows={2} />
        <TextField label="CTA label" value={form.cta_label} onChange={(v) => setForm({ ...form, cta_label: v })} placeholder="Start listening" />
        <Select label="Accent" value={form.accent} onChange={(v) => setForm({ ...form, accent: v })} options={[
          { value: 'emerald', label: 'Emerald' },
          { value: 'gold', label: 'Gold' },
          { value: 'pink', label: 'Pink' },
        ]} />
        {/* Reusable Audio Experience — Phase 19 */}
        <div className="pg-input rounded-[var(--pg-radius-sm)] p-3 space-y-2" data-testid="channel-content-experience">
          <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">Content Experience</p>
          <Select label="Experience" value={form.content_experience || 'standard'} onChange={(v) => setForm({ ...form, content_experience: v })} options={EXPERIENCE_OPTS} />
          <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">
            {form.content_experience === 'audio' && 'Any audio assigned to this channel renders as a streaming card grid with Play All, Shuffle, Queue, and Resume — the same premium experience as the Music channel.'}
            {form.content_experience === 'podcast' && 'Podcast-style card grid with Play All, Queue, and Resume — mirrors the Podcasts channel.'}
            {form.content_experience === 'video' && 'Video-first grid presentation.'}
            {form.content_experience === 'reading' && 'Article-first editorial layout.'}
            {form.content_experience === 'product' && 'Product-first commerce grid.'}
            {(!form.content_experience || form.content_experience === 'standard') && 'Standard mixed layout for any content type assigned to this channel.'}
          </p>
        </div>
        <FileField label="Channel artwork (used in home carousel + tiles)" kind="image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} accept="image/*" />
        <TextField label="Order" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: Number(v) || 0 })} />
        <div className="grid grid-cols-3 gap-3">
          <Toggle label="Enabled" value={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} />
          <Toggle label="Show in nav" value={form.show_in_nav} onChange={(v) => setForm({ ...form, show_in_nav: v })} />
          <Toggle label="Show in carousel" value={form.show_in_carousel} onChange={(v) => setForm({ ...form, show_in_carousel: v })} />
        </div>
        <TemplateAssignmentControl targetType="channel" targetId={form.id} />
      </Drawer>
    </div>
  );
}
